<template>
  <Modal v-model="visible" title="称号" width="820" :footer-hide="true" @on-visible-change="handleVisibleChange">
    <div class="title-system">
      <header class="title-summary" :class="qualityClass(equippedTitle)">
        <div>
          <span>已收集 {{ Number(overview.ownedCount || 0) }} / {{ titles.length }}</span>
          <h2>{{ equippedTitle ? equippedTitle.titleName : '暂未佩戴称号' }}</h2>
          <p>{{ equippedTitle ? equippedTitle.titleDesc : '选择已获得的称号佩戴，属性会立即计入角色。' }}</p>
        </div>
        <Button v-if="equippedTitle" icon="md-close" :loading="operating" @click="unequip">卸下</Button>
      </header>

      <Spin fix v-if="loading" />
      <div v-if="!loading && !titles.length" class="title-empty">暂无可用称号</div>
      <div class="title-list">
        <article
          v-for="title in titles"
          :key="title.titleId"
          class="title-item"
          :class="[qualityClass(title), { locked: !isOwned(title), equipped: isEquipped(title) }]"
        >
          <div class="title-name">
            <span class="title-emblem" aria-hidden="true"><Icon type="ios-ribbon" /></span>
            <div class="title-copy">
              <div class="title-heading">
                <h3>{{ title.titleName }}</h3>
                <Tag :color="qualityColor(title.quality)">{{ qualityName(title.quality) }}</Tag>
              </div>
              <p>{{ title.titleDesc }}</p>
            </div>
          </div>
          <div class="title-bonuses">
            <span v-for="bonus in bonusList(title)" :key="bonus.label">{{ bonus.label }} +{{ bonus.value }}</span>
            <span v-if="!bonusList(title).length">纪念称号</span>
          </div>
          <div class="title-action">
            <Button v-if="isEquipped(title)" size="small" disabled icon="md-checkmark">佩戴中</Button>
            <Button v-else-if="isOwned(title)" size="small" type="primary" :loading="operatingTitleId === title.titleId" @click="equip(title)">佩戴</Button>
            <span v-else class="unlock-hint">{{ unlockText(title) }}</span>
          </div>
        </article>
      </div>
    </div>
  </Modal>
</template>

<script>
export default {
  name: 'TitleSystemModal',
  props: { value: { type: Boolean, default: false } },
  data () {
    return { visible: this.value, loading: false, operating: false, operatingTitleId: null, overview: {} }
  },
  computed: {
    titles () { return Array.isArray(this.overview.titles) ? this.overview.titles : [] },
    equippedTitle () { return this.overview.equippedTitle || null }
  },
  watch: {
    value (next) { this.visible = next; if (next) this.loadOverview() },
    visible (next) { this.$emit('input', next) }
  },
  methods: {
    getCookie (name) {
      const parts = (`; ${document.cookie}`).split(`; ${name}=`)
      return parts.length === 2 ? parts.pop().split(';').shift() : ''
    },
    handleVisibleChange (shown) { if (shown) this.loadOverview() },
    async loadOverview () {
      const charaId = this.getCookie('charaId')
      if (!charaId || this.loading) return
      this.loading = true
      try {
        const res = await this.$http.post(`/gameTitle/overview?charaId=${encodeURIComponent(charaId)}`)
        if (res.data.status === 200) this.overview = res.data.data || {}
        else this.$Message.warning(res.data.msg || '称号加载失败')
      } catch (error) {
        this.$Message.warning('称号加载失败')
      } finally { this.loading = false }
    },
    async equip (title) {
      const charaId = this.getCookie('charaId')
      this.operatingTitleId = title.titleId
      try {
        const res = await this.$http.post(`/gameTitle/equip?charaId=${encodeURIComponent(charaId)}&titleId=${title.titleId}`)
        if (res.data.status === 200) { this.$Message.success('称号已佩戴'); await this.loadOverview(); this.$emit('changed') }
        else this.$Message.warning(res.data.msg || '佩戴失败')
      } catch (error) { this.$Message.warning('佩戴失败') } finally { this.operatingTitleId = null }
    },
    async unequip () {
      const charaId = this.getCookie('charaId')
      this.operating = true
      try {
        const res = await this.$http.post(`/gameTitle/unequip?charaId=${encodeURIComponent(charaId)}`)
        if (res.data.status === 200) { this.$Message.success('称号已卸下'); await this.loadOverview(); this.$emit('changed') }
        else this.$Message.warning(res.data.msg || '卸下失败')
      } catch (error) { this.$Message.warning('卸下失败') } finally { this.operating = false }
    },
    isOwned (title) { return Number(title.owned) === 1 },
    isEquipped (title) { return Number(title.equipped) === 1 },
    qualityClass (title) { return `title-quality-${Number(title && title.quality || 0)}` },
    unlockText (title) { return title.unlockType === 'ROLE_LEVEL' ? `${title.unlockValue}级解锁` : '尚未获得' },
    qualityName (quality) { return ['普通', '精良', '稀有', '史诗', '传说'][Number(quality || 1) - 1] || '普通' },
    qualityColor (quality) { return ['default', 'green', 'blue', 'purple', 'gold'][Number(quality || 1) - 1] || 'default' },
    bonusList (title) {
      const fields = [
        ['physicalAttack', '力攻'], ['magicAttack', '灵攻'], ['health', '生命'], ['defense', '防御'],
        ['magicDefense', '法防'], ['physique', '根骨'], ['spirit', '灵力'], ['dexterous', '力道'],
        ['lucky', '幸运'], ['criticalHitLevel', '暴击']
      ]
      const result = fields.filter(field => Number(title[field[0]] || 0) !== 0).map(field => ({ label: field[1], value: title[field[0]] }))
      if (Number(title.damageBoost || 0)) result.push({ label: '增伤', value: `${(Number(title.damageBoost) * 100).toFixed(1)}%` })
      if (Number(title.damageDeduct || 0)) result.push({ label: '减伤', value: `${(Number(title.damageDeduct) * 100).toFixed(1)}%` })
      return result
    }
  }
}
</script>

<style scoped>
.title-system {
  position: relative;
  min-height: 260px;
  color: #30343b;
}

.title-summary,
.title-item {
  --title-accent: #8b929b;
  --title-accent-deep: #59616d;
  --title-surface: #f4f6f8;
  --title-surface-end: #e9edf1;
  --title-border: #cbd1d8;
  --title-glow: rgba(79, 88, 99, 0.12);
}

.title-quality-1 {
  --title-accent: #8b929b;
  --title-accent-deep: #59616d;
  --title-surface: #fafbfc;
  --title-surface-end: #edf0f3;
  --title-border: #cbd1d8;
  --title-glow: rgba(79, 88, 99, 0.12);
}

.title-quality-2 {
  --title-accent: #3c9b68;
  --title-accent-deep: #17623b;
  --title-surface: #f2faf5;
  --title-surface-end: #ddf0e5;
  --title-border: #9bcdb0;
  --title-glow: rgba(38, 126, 78, 0.16);
}

.title-quality-3 {
  --title-accent: #378dc8;
  --title-accent-deep: #1d5d91;
  --title-surface: #f0f8fd;
  --title-surface-end: #dceefa;
  --title-border: #91bfdf;
  --title-glow: rgba(37, 110, 161, 0.18);
}

.title-quality-4 {
  --title-accent: #9257bd;
  --title-accent-deep: #67328d;
  --title-surface: #faf4fd;
  --title-surface-end: #ecdef5;
  --title-border: #c4a1da;
  --title-glow: rgba(116, 63, 151, 0.2);
}

.title-quality-5 {
  --title-accent: #c18a24;
  --title-accent-deep: #7f4e08;
  --title-surface: #fffaf0;
  --title-surface-end: #f5e5bb;
  --title-border: #d8b55e;
  --title-glow: rgba(176, 119, 20, 0.24);
}

.title-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 18px 20px;
  border: 1px solid var(--title-border);
  border-left: 5px solid var(--title-accent);
  border-radius: 6px;
  background:
    linear-gradient(105deg, rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.18)),
    linear-gradient(135deg, var(--title-surface), var(--title-surface-end));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 5px 14px var(--title-glow);
}

.title-summary::after {
  content: '';
  position: absolute;
  top: -34px;
  right: 76px;
  width: 88px;
  height: 130px;
  border-left: 1px solid rgba(255, 255, 255, 0.72);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: rotate(24deg);
  pointer-events: none;
}

.title-summary > * {
  position: relative;
  z-index: 1;
}

.title-summary span {
  color: var(--title-accent-deep);
  font-size: 12px;
  font-weight: 600;
}

.title-summary h2 {
  margin: 4px 0;
  color: var(--title-accent-deep);
  font-size: 22px;
}

.title-summary p,
.title-name p {
  margin: 0;
  color: #68717b;
}

.title-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.title-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  position: relative;
  overflow: hidden;
  min-height: 142px;
  padding: 14px;
  border: 1px solid var(--title-border);
  border-radius: 6px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.22)),
    linear-gradient(135deg, var(--title-surface), var(--title-surface-end));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92), 0 4px 12px var(--title-glow);
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}

.title-item::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: var(--title-accent);
  box-shadow: 1px 0 7px var(--title-glow);
}

.title-item:not(.locked):hover {
  transform: translateY(-1px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95), 0 7px 18px var(--title-glow);
}

.title-item.equipped {
  border-color: var(--title-accent);
  box-shadow: inset 0 0 0 1px var(--title-accent), 0 7px 18px var(--title-glow);
}

.title-item.equipped::after {
  content: '◆';
  position: absolute;
  right: 9px;
  bottom: 5px;
  color: var(--title-accent);
  font-size: 16px;
  opacity: .35;
}

.title-item.locked {
  filter: saturate(.35);
  opacity: .7;
}

.title-name {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  min-width: 0;
}

.title-emblem {
  display: inline-flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.76);
  border-radius: 50%;
  background: linear-gradient(145deg, var(--title-accent), var(--title-accent-deep));
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, .42), 0 3px 8px var(--title-glow);
  font-size: 18px;
}

.title-copy {
  min-width: 0;
}

.title-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
}

.title-name h3 {
  margin: 0;
  color: var(--title-accent-deep);
  font-size: 16px;
}

.title-bonuses {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 43px;
}

.title-bonuses span {
  padding: 2px 7px;
  color: var(--title-accent-deep);
  border: 1px solid var(--title-border);
  border-radius: 3px;
  background: rgba(255, 255, 255, .58);
  font-size: 12px;
}

.title-action {
  position: relative;
  z-index: 1;
  min-width: 58px;
  text-align: right;
}

.unlock-hint,
.title-empty {
  color: #777f89;
  font-size: 12px;
}

.title-empty {
  padding: 60px 0;
  text-align: center;
}

@media (max-width: 700px) {
  .title-list { grid-template-columns: 1fr; }
  .title-summary { align-items: flex-start; }
  .title-item { min-height: 132px; }
}
</style>



// WEBPACK FOOTER //
// src/components/TitleSystemModal.vue